// SPDX-License-Identifier: AGPL-3.0-or-later
// Copyright (C) 2026 Ben Richardson
// Bot bluffing model. Solo-only (host-run). Deterministic per decision so the
// balance sim is reproducible and P2P host-run bots agree across a takeover.
import { makeRng } from '@ben-gy/game-engine/rng';
import type { CritterType, PublicState, PrivateView } from './game';

export interface BotProfile {
  id: string;
  name: string;
  /** Chance of declaring a LIE when giving. */
  lieRate: number;
  /** How strongly to target the most-endangered opponent (0..1). */
  aggression: number;
  /** Base tendency to call bluff — higher = more suspicious. */
  doubt: number;
  /** Tendency to peek-and-pass when unsure and it is legal. */
  peekiness: number;
  /**
   * Sim-only override: force every receive to a single rote action, so the
   * balance test can prove no rote script dominates. Undefined for real bots.
   */
  forceReceive?: 'truth' | 'bluff' | 'peek';
}

/** Named personalities the solo game deals. The player learns each one's tell. */
export const PROFILES: readonly Omit<BotProfile, 'id'>[] = [
  { name: 'Honest Ada', lieRate: 0.24, aggression: 0.45, doubt: 0.42, peekiness: 0.25 },
  { name: 'Sly Cove', lieRate: 0.56, aggression: 0.8, doubt: 0.5, peekiness: 0.55 },
  { name: 'Reckless Bo', lieRate: 0.46, aggression: 0.55, doubt: 0.68, peekiness: 0.2 },
  { name: 'Cagey Wren', lieRate: 0.36, aggression: 0.62, doubt: 0.58, peekiness: 0.45 },
  { name: 'Nib', lieRate: 0.5, aggression: 0.5, doubt: 0.5, peekiness: 0.4 },
  { name: 'Trot', lieRate: 0.3, aggression: 0.7, doubt: 0.46, peekiness: 0.5 },
];

/** A steady, competent reader — the yardstick the sim measures rote scripts against. */
export const BALANCED: Omit<BotProfile, 'id' | 'name'> = {
  lieRate: 0.42,
  aggression: 0.62,
  doubt: 0.5,
  peekiness: 0.4,
};

export function botRng(pub: PublicState, id: string, salt: string): () => number {
  return makeRng(`${pub.seed}:${pub.ply}:${id}:${salt}`);
}

const alive = (pub: PublicState): PublicState['seats'] => pub.seats.filter((s) => s.alive);

/** The type a seat is most dangerously piled up in (or null if empty). */
function scaryTypeFor(pub: PublicState, id: string): CritterType | null {
  const s = pub.seats.find((x) => x.id === id);
  if (!s || s.menagerie.length === 0) return null;
  const counts = new Map<CritterType, number>();
  for (const c of s.menagerie) counts.set(c.type, (counts.get(c.type) ?? 0) + 1);
  let best: CritterType | null = null;
  let n = 0;
  for (const [t, k] of counts) if (k > n) ((n = k), (best = t));
  return best;
}

export interface GiveChoice {
  cardId: number;
  to: string;
  claim: CritterType;
}

/** Pick a target: the most-endangered opponent when aggressive, else at random. */
function pickTarget(pub: PublicState, myId: string, p: BotProfile, rng: () => number): string {
  const opp = alive(pub).filter((s) => s.id !== myId);
  if (opp.length === 0) return myId;
  if (rng() < p.aggression) {
    let best = opp[0];
    for (const s of opp) if (s.maxCount > best.maxCount) best = s;
    // Break ties among the most-endangered randomly so it is not always seat-0.
    const top = opp.filter((s) => s.maxCount === best.maxCount);
    return top[Math.floor(rng() * top.length)].id;
  }
  return opp[Math.floor(rng() * opp.length)].id;
}

/** Choose a card + claim toward `to`, lying at the profile's rate. `hand` is mine. */
function claimToward(
  pub: PublicState,
  hand: PrivateView['hand'],
  to: string,
  p: BotProfile,
  rng: () => number,
): { cardId: number; claim: CritterType } {
  const types = pub.rules.types;
  const scary = scaryTypeFor(pub, to) ?? types[Math.floor(rng() * types.length)];
  const lie = rng() < p.lieRate;

  if (lie) {
    // Declare the scary type but hand over a card that ISN'T it, where possible.
    const notScary = hand.filter((c) => c.type !== scary);
    const card = (notScary.length ? notScary : hand)[Math.floor(rng() * (notScary.length || hand.length))];
    const claim = card.type === scary
      ? types.filter((t) => t !== card.type)[Math.floor(rng() * (types.length - 1))] ?? scary
      : scary;
    return { cardId: card.id, claim };
  }
  // Truthful threat: prefer a real card of the scary type if we hold one.
  const real = hand.filter((c) => c.type === scary);
  const card = (real.length ? real : hand)[Math.floor(rng() * (real.length || hand.length))];
  return { cardId: card.id, claim: card.type };
}

export function chooseGive(pub: PublicState, myId: string, priv: PrivateView, p: BotProfile): GiveChoice | null {
  if (priv.hand.length === 0) return null;
  const to = pickTarget(pub, myId, p, botRng(pub, myId, 'gt'));
  const { cardId, claim } = claimToward(pub, priv.hand, to, p, botRng(pub, myId, 'gc'));
  return { cardId, to, claim };
}

export type ReceiveChoice = { action: 'truth' } | { action: 'bluff' } | { action: 'peek' };

/** Estimate P(the current claim is true) from the claimer's tell + the trail length. */
function pClaimTrue(pub: PublicState): number {
  const f = pub.inFlight;
  if (!f) return 0.5;
  const claimer = pub.seats.find((s) => s.id === f.from);
  const t = claimer?.tell ?? { truths: 0, lies: 0 };
  const honesty = (t.truths + 1) / (t.truths + t.lies + 2); // Beta(1,1) prior
  // A card that has been peeked and passed on is a little more suspect: a holder
  // chose to dump the danger rather than resolve it.
  const hops = Math.max(0, f.trail.length - 1);
  return honesty * Math.pow(0.85, hops);
}

/**
 * Decide truth / bluff / peek — WITHOUT knowing the true card (a receiver is never
 * in the trail, so `priv.inFlightTrueType` is null here; that is the point). Peeking
 * is the only way to learn the truth, and it happens after this returns 'peek'.
 */
export function chooseReceive(
  pub: PublicState,
  myId: string,
  _priv: PrivateView,
  p: BotProfile,
  peekLegal: boolean,
  peekTargets: string[],
): ReceiveChoice {
  const f = pub.inFlight!;
  const canOffload = peekLegal && peekTargets.length > 0;
  if (p.forceReceive === 'peek' && canOffload) return { action: 'peek' };
  if (p.forceReceive === 'truth') return { action: 'truth' };
  if (p.forceReceive === 'bluff') return { action: 'bluff' };

  const me = pub.seats.find((s) => s.id === myId)!;
  const myClaimCount = me.menagerie.filter((c) => c.type === f.claim).length;
  const nearInClaim = myClaimCount >= pub.rules.threshold - 1;

  let pTrue = pClaimTrue(pub);
  pTrue -= (p.doubt - 0.5) * 0.28; // a suspicious bot discounts claims

  const rng = botRng(pub, myId, 'rc');

  // Uncertain, or one-from-out in the claimed type, and able to hand the danger on?
  const uncertain = Math.abs(pTrue - 0.5) < 0.18;
  if (canOffload && (nearInClaim || uncertain) && rng() < p.peekiness) return { action: 'peek' };

  // Guessing 'truth' when wrong means taking a NON-claim card (safe from the claim
  // type); guessing 'bluff' when wrong means taking a claim card. So a bot that is
  // one-from-out in the claimed type must not doubt it unless very confident it's a lie.
  let believe = pTrue >= 0.5;
  if (nearInClaim && pTrue > 0.28) believe = true;
  return { action: believe ? 'truth' : 'bluff' };
}

/** After a peek reveals `trueType`, choose where to pass it and what to claim. */
export function choosePeek(
  pub: PublicState,
  myId: string,
  trueType: CritterType,
  p: BotProfile,
  peekTargets: string[],
): { to: string; claim: CritterType } {
  const rng = botRng(pub, myId, 'pk');
  const to = peekTargets[Math.floor(rng() * peekTargets.length)];
  const scary = scaryTypeFor(pub, to) ?? trueType;
  const lie = rng() < p.lieRate;
  const claim = lie
    ? scary === trueType
      ? pub.rules.types.find((t) => t !== trueType) ?? scary
      : scary
    : trueType;
  return { to, claim };
}
